"use client";

import { LockIcon, ServerIcon } from "lucide-react";
import { Dispatch, SetStateAction, useMemo } from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow,
} from "@/components/ui/table";
import {
  RoomUsageListResponse,
  RoomUsageListResponseItem,
} from "@classview/shared-types";
import {
  PaginationState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

interface RoomSystemUsageTableProps {
  data: RoomUsageListResponse;
  pagination: PaginationState;
  onPaginationChange: Dispatch<SetStateAction<PaginationState>>;
  pageCount?: number;
  isLoading?: boolean;
}

export function RoomSystemUsageTable({
  data,
  pagination,
  onPaginationChange,
  pageCount,
  isLoading,
}: RoomSystemUsageTableProps) {
  const columnHelper = createColumnHelper<RoomUsageListResponseItem>();

  const columns = useMemo(
    () => [
      columnHelper.accessor("organisation", {
        cell: (info) => info.row.original.organisation.name ?? "-",
        header: () => <span>Organisation</span>,
        size: 150,
      }),
      columnHelper.accessor("roomSystem.name", {
        cell: (info) => (
          <>
            <span className="block truncate">{info.getValue()}</span>
            <span className="flex text-xs capitalize">
              {info.row.original.roomSystem.visiblity === "PRIVATE" ? (
                <LockIcon className="mr-1 size-4 text-green-600" />
              ) : (
                <ServerIcon className="mr-1 size-4 text-blue-500" />
              )}
              {info.row.original.roomSystem.visiblity.toLowerCase()}
            </span>
          </>
        ),
        header: () => <span>Name</span>,
        enableSorting: true,
        size: 999,
      }),

      columnHelper.accessor("roomSystem.type", {
        cell: (info) => {
          const type = info.getValue().toLowerCase().replace("_", " ");
          return <span className="truncate capitalize">{type}</span>;
        },
        header: () => <span>Room System Type</span>,
        size: 150,
      }),
      columnHelper.display({
        id: "sessions",
        cell: (info) => {
          return (
            <>
              <p>{info.row.original.internal + info.row.original.external}</p>
              <p className="block text-muted">
                {info.row.original.external} external
              </p>
            </>
          );
        },
        header: () => <span>Sessions</span>,
        size: 150,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: pagination.pageSize,
        pageIndex: pagination.pageIndex,
      },
    },
    state: {
      pagination,
    },
    defaultColumn: {
      size: 999,
    },
    manualPagination: true,
    pageCount: pageCount,
    onPaginationChange: onPaginationChange,
  });

  return (
    <Table className="border-0">
      <TableHeader className="border-0">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow className="hover:bg-transparent" key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead
                  key={header.id}
                  style={{
                    width: header.getSize() === 999 ? "auto" : header.getSize(),
                  }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={columns.length} className="h-24 text-center">
              No results.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
      <TableFooter>
        <TablePagination table={table} isLoading={isLoading} />
      </TableFooter>
    </Table>
  );
}
